import * as React from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { animated, useSpring } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { BackgroundLoad } from '../../../layout/BackgroundLoad';
import { Thanks } from '../../../layout/Thanks';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Button } from '../../../ui/button/Button';

import { AccordionBenefits } from '../../../partials/AccordionBenefits';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { createLeadMutation } from '../../../../../client/__graphql__/mutations';
import { ILeadInput, ILeadResponse } from '../../../../__types__/global';
import { useProgressiveImage } from '../../../../utils/hooks/useProgressiveImage';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const MeglerStjerne: React.FC<{ buttonLabel: string }> = ({ buttonLabel }) => {
  const location = useLocation();
  const [showRules, setShowRules] = React.useState(false);
  const { submitWithDigger } = useDigtectiveContext();
  const { routeConfig } = React.useContext(SiteContext);
  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const MAIL_MUTATION = createLeadMutation({
    mail: {
      response: `{
            success
          }`
    }
  });

  const [mail, { loading: mailLoading, error }] = useMutation<
    ILeadResponse,
    ILeadInput
  >(MAIL_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead.mail?.success) {
        setPosted(true);
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse',
        required: false
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        mail({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: 'EPOST',
              mailType: 63
            }
          }
        });
      });
    }
  });
  const Top: React.FC<{ url: string }> = ({ url }) => {
    const { loaded } = useProgressiveImage({
      url
    });
    const animatedProps = useSpring({
      from: { opacity: 0 },
      to: { opacity: loaded ? 1 : 0 }
    });

    return (
      <StyledTop style={animatedProps}>
        <img src={url} alt="bilde" />
      </StyledTop>
    );
  };
  const StyledTop = styled(animated.div)`
    margin-top: 66px;
    width: 100%;
    overflow: hidden;

    img {
      width: 100%;
      max-height: 500px;
      object-fit: contain;
      object-position: center;
    }

    @media all and (max-width: 1280px) {
      img {
        min-height: 300px;
        object-fit: cover;
      }
    }
  `;

  return (
    <>
      <Seo title="" description="" noIndexActivated />

      <Wrapper
        style={{
          paddingTop: '3em'
        }}
      >
        <Section className="form-section" style={{ paddingBottom: '10px' }}>
          <Heading tag="h1" className="star-title" center={true}>
            Er du vår neste stjerne?
          </Heading>
          <BigSpaceBetweenTitleImageAndSubTitle />
          <Section
            className="form-section text"
            style={{ paddingBottom: '10px' }}
          >
            <Heading tag="h2" center={true}>
              I 2023 skal alle få se
            </Heading>

            <Paragraph center={true} className="form-text">
              I 2023 skal vi gjennom en dokumentarserie vise verdien av
              PrivatMegleren. Oppgaven er å selge fire ulike eiendommer som av
              en eller annen grunn ikke har blitt solgt. Dette er ekte
              eiendommer og ekte mennesker som trenger vår hjelp.
            </Paragraph>

            <Paragraph center={true} className="form-text">
              Derfor søker vi etter{' '}
              <span className="red-marking">fire meglerstjerner</span> som kan
              tenke seg en hovedrolle i oppgaven med å omsette årets mest
              synlige salgsoppdrag. Det blir mye skjermtid og oppmerksomhet på
              de utvalgte meglerne og kontorene de jobber på.
            </Paragraph>

            <Paragraph center={true} className="form-text">
              Vi skal vise at vi har de beste meglerne, de beste verktøyene og
              den beste leveransen. Uansett marked.
            </Paragraph>

            <Paragraph center={true} className="form-text bold-white">
              Har du det som skal til?
            </Paragraph>
            <Paragraph center={true} className="form-text">
              Klikk på knappen under, fyll inn skjemaet og send oss en{' '}
              <span className="red-marking">video</span>. Kanskje nettopp du er
              vår neste stjerne!
            </Paragraph>
            <Paragraph
              style={{ paddingBottom: '2em' }}
              center={true}
              className="form-text bold-white"
            >
              Søknadsfristen er 28. februar
            </Paragraph>
          </Section>
        </Section>
      </Wrapper>

      <RSVP>
        <Section className="form-section">
          <ButtonGrid>
            <ButtonGridColumn>
              <Button
                type="primary"
                colorTheme="gold"
                as="a"
                target="_blank"
                href="https://form.jotform.com/223423645278055"
              >
                {buttonLabel ? buttonLabel : 'Påmeldingsskjema'}
              </Button>
            </ButtonGridColumn>
          </ButtonGrid>
        </Section>
      </RSVP>
      <Thanks style={fadeIn}>
        <Section className="form-section" style={{ paddingTop: 0 }}>
          <AccordionBenefits />
        </Section>
      </Thanks>

      <Grid className="grid">
        <Column>
          <Heading tag="h2" className="title" center={true}>
            Team og jury
          </Heading>
          <Paragraph center={true} className="form-text subtitle">
            Fagjuryen
          </Paragraph>
          <Paragraph center={true} className="form-text fixed-width">
            Det er en rekke personer involvert, både for å velge eiendommene og
            meglerne. I fagjuryen er det representanter fra PrivatMeglerens
            hovedkontor, reklamebyrået og filmproduksjonsbyrået. Disse skal
            sikre at vi velger ut de meglerne som kan representere hele kjeden i
            henhold til vår merkevare.
          </Paragraph>
          <Paragraph center={true} className="form-text subtitle">
            Hva krever vi av deg og ditt kontor?
          </Paragraph>
          <Paragraph center={true} className="form-text fixed-width">
            Hovedkontoret stiller med juridisk bistand i alt fra utvelgelse av
            objekter, til tilpassede oppdragsavtaler og eventuelle konflikter.
            Vi vil også stille med midler til markedsføring av objektene og
            dekke innkjøp til for eksempel enkel oppussing. Detaljene rundt
            dette vil vi diskutere nærmere med de utvalgte meglerne og kontorene
            deres.
          </Paragraph>
          <Paragraph center={true} className="form-text fixed-width">
            Som megler må du avklare din deltakelse og søknad med daglig leder.
            Daglig leder eller fagansvarlig på kontoret vil måtte gå god for din
            deltakelse og følge prosessen. Honoraret vil være eksponeringen du
            får, ikke et vanlig meglerhonorar. På samme måte vil teamet du
            bruker som stylist, fotograf, snekker etc. få betalt i form av
            eksponering. Vi vil ikke dekke tiden de bruker, men vil som nevnt
            dekke innkjøpene deres som maling, materialer osv. Se på dette som
            en kjempemulighet til å få vist frem deg selv, kontoret og dine
            beste samarbeidspartnere. Vi skal få dere til å skinne.
          </Paragraph>
        </Column>
        <Column>
          <Heading tag="h2" className="title" center={true}>
            Hvor og når?
          </Heading>
          <Paragraph center={true} className="form-text subtitle">
            Søknadsprosess
          </Paragraph>
          <Paragraph center={true} className="form-text fixed-width">
            Din videosøknad vurderes av fagjuryen. Du får raskt beskjed om du
            kommer videre til neste runde. Du kalles inn til et videomøte med
            juryen, hvor målet er å bli bedre kjent med deg. Utmerker du deg,
            tar vi kontakt så snart et passende oppdrag kommer inn. Selve
            filmingen vil igangsettes etter nærmere avtale med deg og ditt
            kontor. Mest sannsynlig i perioden mars-oktober i år.
          </Paragraph>
          <Paragraph center={true} className="form-text subtitle">
            Hva skal filmes?
          </Paragraph>
          <Paragraph center={true} className="form-text fixed-width">
            Vi kommer til å filme deg i flere settinger, både på kontoret og ute
            på eiendommen sammen med kunden. Det aller viktigste opptaket skjer
            etter at salget er gjennomført, slik at du kan gå inn i intervjuet
            med selvsikkerhet og fremstå fra din aller beste side. Dette får du
            selvsagt vite mer om.
          </Paragraph>
        </Column>
      </Grid>

      <BackgroundLoad
        hideGradient
        showOnMobile
        opacity={0.8}
        url="https://cdn.reeltime.no/pm_assets/kampanjer/meglerstjerne/stjerne.jpg"
      />
    </>
  );
};

const FormWrapper = styled(animated.div)`
  .bold {
    font-weight: bold;
  }
  .questions {
    color: red;
    font-weight: bold;
  }
`;

const Wrapper = styled.div`
  .star-title {
    color: #fc5355;
  }
  .bold-white {
    color: ${({ theme }) => theme.colors.white};
    margin-bottom: -15px;
    font-weight: bold;
    font-size: 1.2rem;
  }
  .red-marking {
    color: #fc5355;
  }
  .text {
    padding: unset;
  }

  @media all and (max-width: 580px) {
    .text {
      padding: 0 3rem;
    }
  }
`;

const RSVP = styled.div`
  background-color: #2f2f2e;
  padding: 1em;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 50px;
  align-items: start;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  margin-top: 4em;
  .fixed-width {
    padding: 0 3rem;
  }
  .title {
    padding-bottom: 2rem;
  }

  .subtitle {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: -15px;
    font-weight: bold;
    font-size: 1.2rem;
  }

  @media all and (max-width: 1000px) {
    grid-template-columns: 1fr;
    grid-column-gap: 50px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-flow: column;
  text-align: center;
`;

const BigSpaceBetweenTitleImageAndSubTitle = styled.div`
  position: relative;
  height: 460px;
`;

const ButtonGridColumn = styled.div`
  display: block;
  align-self: center;
  padding-top: 10px;
`;

const ButtonGrid = styled.div`
  display: grid;
  width: 100%;
  justify-content: center;
  grid-template-columns: repeat(2, max-content);
  grid-column-gap: 10px;

  @media all and (max-width: 330px) {
    grid-template-columns: max-content;
    grid-row-gap: 10px;
  }
`;
export default MeglerStjerne;
